import { useState, useRef, useEffect, useMemo } from 'react';
import {
  Text,
  Code,
  Icon,
  InlineStack,
  FormattedDateTime,
  Spinner,
  Flex,
  PlainButton,
  Divider,
} from '@customerio/pluma-components/react';
import type { SearchResult } from '@/services/auth/types';
import { hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { formatNumber } from '@/utils/number';
import { parseConditions, ConditionNodeRenderer } from '@/features/condition-viewer';
import { MetadataRow } from './metadata-row';
import { SEGMENT_TYPE_LABELS } from './constants';
import * as styles from './index.css';

type Props = { result: Extract<SearchResult, { kind: 'segment' }> };

export function SegmentPreview({ result }: Props) {
  const taxonomy = useProductTaxonomy();
  const envId = Number(result.other.env_id);
  const segmentId = Number(result.other.id);
  const isDataDriven = result.other.segment_type !== 'static';
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const fadeRef = useRef<HTMLDivElement>(null);

  const {
    data: segmentData,
    isLoading: isSegmentLoading,
    isError: isSegmentError,
  } = useHydraUIAPIQuery({
    queryKey: ['getSegmentById', envId, segmentId],
    queryFn: ({ signal }) =>
      hydraUiApi.segments.get({
        params: { path: { environment_id: String(envId), segment_id: String(segmentId) } },
        signal,
      }),
    queryConfig: { enabled: isDataDriven && envId > 0 && segmentId > 0, staleTime: 5 * 60 * 1000 },
  });

  const { data: countData, isLoading: isCountLoading } = useHydraUIAPIQuery({
    queryKey: ['getSegmentCount', envId, segmentId],
    queryFn: ({ signal }) =>
      hydraUiApi.segments.getCount({
        params: { path: { environment_id: String(envId), segment_id: String(segmentId) } },
        signal,
      }),
    queryConfig: { enabled: envId > 0 && segmentId > 0, staleTime: 5 * 60 * 1000 },
  });

  const segment = segmentData?.segment;
  const conditions = useMemo(
    () => (segment?.conditions != null ? parseConditions(segment.conditions) : null),
    [segment?.conditions],
  );

  useEffect(() => {
    setIsExpanded(false);
    setIsOverflowing(false);
  }, [segmentId]);

  const isExpandedRef = useRef(isExpanded);
  isExpandedRef.current = isExpanded;

  useEffect(() => {
    const element = fadeRef.current;
    if (element == null) return;

    const observer = new ResizeObserver(() => {
      if (!isExpandedRef.current) {
        setIsOverflowing(element.scrollHeight > element.clientHeight);
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [conditions]);

  return (
    <>
      {isDataDriven ? (
        <>
          {isSegmentLoading ? (
            <Flex alignItems="center" justifyContent="center" py="200">
              <Spinner />
            </Flex>
          ) : isSegmentError ? (
            <Text size="xs" color="subtle">
              Failed to load conditions
            </Text>
          ) : conditions != null ? (
            <Flex direction="column" gap="100">
              <div
                ref={fadeRef}
                className={`${styles.conditionsFade} ${
                  isOverflowing && !isExpanded ? styles.conditionsFadeGradient : ''
                } ${isExpanded ? styles.conditionsFadeExpanded : ''}`}
              >
                <div className={styles.conditionsContainer}>
                  <ConditionNodeRenderer node={conditions} workspaceId={envId} />
                </div>
              </div>
              {isOverflowing ? (
                <PlainButton onClick={() => setIsExpanded((previous) => !previous)}>
                  <Text size="xs" color="accent">
                    {isExpanded ? 'Show less' : 'Show more'}
                  </Text>
                </PlainButton>
              ) : null}
            </Flex>
          ) : (
            <Text size="xs" color="subtle">
              No conditions
            </Text>
          )}
          <Divider mt="100" />
        </>
      ) : null}
      <Flex direction="column" gap="100" mt={isDataDriven ? '100' : undefined}>
        <MetadataRow label="ID">
          <Code size="xs">{result.other.id}</Code>
        </MetadataRow>
        <MetadataRow label="Type">
          <InlineStack gap="050" alignBlock="center">
            <Icon
              name={result.other.segment_type === 'static' ? 'segment-manual' : 'segment-data-driven'}
              size="sm"
              color="subtle"
            />
            <Text size="xs">{SEGMENT_TYPE_LABELS[result.other.segment_type] ?? 'Segment'}</Text>
          </InlineStack>
        </MetadataRow>
        <MetadataRow label={taxonomy.term('people.titlePlural')}>
          {isCountLoading ? (
            <Spinner />
          ) : (
            <Text size="xs">{countData?.count != null ? formatNumber(countData.count) : '—'}</Text>
          )}
        </MetadataRow>
        <MetadataRow label="Created">
          <Text size="xs">
            <FormattedDateTime className="fs-mask" date={result.other.created_at} format="medium" />
          </Text>
        </MetadataRow>
        <MetadataRow label="Updated">
          <Text size="xs">
            <FormattedDateTime className="fs-mask" date={result.other.updated_at} format="medium" />
          </Text>
        </MetadataRow>
        {result.other.archived === 'true' ? (
          <MetadataRow label="Status">
            <InlineStack gap="050" alignBlock="center">
              <Icon name="archive" size="sm" color="subtle" />
              <Text size="xs" color="subtle">
                Archived
              </Text>
            </InlineStack>
          </MetadataRow>
        ) : null}
      </Flex>
    </>
  );
}
